import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import CheckBox from '~/Common-Objects/UI/Forms/CheckBox/CheckBox'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import TextInput from '~/Common-Objects/UI/Forms/TextInput/TextInput'
import axios from '~/data/http/axios'
import EmailValidator from '~/Classes/emailValidator'
import GoogleAnalytics from '~/Classes/googleAnalytics'
import PostHogFunctions from '~/Classes/posthog'
import LandingPageTemplate from '~/pages/login/template'
import i18n from '~/i18n'
import { isPasswordStrongEnough } from '~/utils/passwordPolicy'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'
import CapsuleSetupContacts from './contacts'
import CapsuleSetupImporting from './importing'
import CapsuleLogo from './capsule.svg'
import TranspondLogo from './transpond.svg'

async function createDefaultGroup(user, company, basic) {
    const obj = {
        groupName: 'Capsule',
        friendlyName: 'Contacts',
        defaultFromName: company,
        signupReminderText: 'Added from Capsule CRM'
    }
    const domain = user.emailAddress.split('@')[1]
    const checker = await axios.get('/accountMaster/domain/checker?domain=' + domain)
    if (checker.data.free || checker.data.disposable) {
        obj.defaultFromEmailAddress = user.emailAddress
    } else {
        obj.defaultFromEmailAddress = 'noreply@' + domain
    }
    if (basic) return obj
    const res = await axios.post('/group', obj)
    return res.data.group
}

export default function SetupCapsule({ onFinish = () => {}, inline = false }) {
    const [password, setPassword] = useState('')
    const [passwordError, setPasswordError] = useState('')

    const [showGroupOptions, setShowGroupOptions] = useState(null)
    const [showPasswordOptions, setShowPasswordOptions] = useState(true)

    const [app, setApp] = useState(null)
    const [status, setStatus] = useState({})

    const [groups, setGroups] = useState(null)
    const [group, setGroup] = useState(null)
    const [newGroup, setNewGroup] = useState({})

    const [syncing, setSyncing] = useState({ tags: 0, lists: 0, fields: 0, contacts: 0 })
    const [importJob, setImportJob] = useState(null)
    const [showContactOptions, setShowContactOptions] = useState(false)
    const [importStartTime, setImportStartTime] = useState(null)

    const importTimer = useRef(null)

    const account = useSelector((state) => state.accountMaster)
    const user = useSelector((state) => state.user)

    const [newsletterChecked, setNewsletterChecked] = useState(false)

    const savePassword = async () => {
        let cont = false
        setPasswordError('')
        if (!isPasswordStrongEnough(password)) {
            setPasswordError(i18n.t('login:reset.strong'))
            return false
        }
        if (newsletterChecked) {
            try {
                let nFirstName = user ? user.userData.firstName : ''
                let nEmail = user ? user.userData.emailAddress : ''
                axios.post('/accountUser/newslettersignup', { firstName: nFirstName, emailAddress: nEmail })
            } catch (err) {}
        }
        try {
            await axios.put('/accountUser', { password, skipLogout: true })
            setShowPasswordOptions(false)
            cont = true
        } catch (e) {
            setPasswordError(e.data.error)
        }
        return cont
    }

    const savePasswordAndGroup = async () => {
        let cont = true
        if (showPasswordOptions) {
            cont = await savePassword()
        }
        if (cont && showGroupOptions) {
            await saveGroup()
        }
        GoogleAnalytics.tagManagerEvent(
            'Capsule_Register_PasswordConfirm',
            account.id,
            user.accountUserId,
            user?.userData?.emailAddress
        )
        PostHogFunctions.capture('onboarding_password_set')
    }

    const saveGroup = async () => {
        try {
            const err = {}
            if (!newGroup.groupName || newGroup.groupName.length < 3) err.groupName = 'Invalid Group Name'
            if (!newGroup.friendlyName || newGroup.friendlyName.length < 3) err.friendlyName = 'Invalid Friendly Name'
            if (!newGroup.defaultFromName || newGroup.defaultFromName.length < 3)
                err.defaultFromName = 'Invalid From Name'
            if (
                !newGroup.defaultFromEmailAddress ||
                newGroup.defaultFromEmailAddress.length < 3 ||
                !EmailValidator.validateEmail(newGroup.defaultFromEmailAddress)
            ) {
                err.defaultFromEmailAddress = 'Invalid From Address'
            }
            if (!newGroup.signupReminderText || newGroup.signupReminderText.length < 3)
                err.signupReminderText = 'Invalid Reminder Text'

            if (Object.keys(err).length) return

            const res = await axios.post('/group', newGroup)
            setGroup(res.data.group.id)
            await axios.put('/application/account/' + app.id, {
                appSettings: { ...app.appSettings, groupId: res.data.group.id }
            })
            setShowGroupOptions(false)
        } catch (e) {
            if (e.data.error.existingGroupId) {
                await axios.put('/application/account/' + app.id, {
                    appSettings: { ...app.appSettings, groupId: e.data.error.existingGroupId }
                })
                setShowGroupOptions(false)
            } else {
                setGroupError((e) => ({ ...e, groupName: e.data.error }))
            }
        }
    }

    const getStatus = async () => {
        const res = await axios.get('/application/capsule/status')
        setStatus(res.data)
    }

    const startSync = async (force) => {
        try {
            const res = await axios.get('/group/' + group + '/import/inprogress')
            if (res.data && !force) {
                setImportJob(res.data)
                setSyncing({ tags: 2, lists: 2, fields: 2, contacts: res.data.importStatus >= 20 ? 2 : 1 })
                await getStatus()
            } else {
                setImportStartTime(Date.now())
                setSyncing({ tags: 0, lists: 0, fields: 0, contacts: 0 })
                setImportJob(null)
                setSyncing((s) => ({ ...s, tags: 1 }))
                const tagsSync = await axios.get('/application/capsule/synctags')
                setStatus((s) => ({ ...s, tags: tagsSync.data.total }))
                setSyncing((s) => ({ ...s, tags: 2 }))

                setSyncing((s) => ({ ...s, fields: 1 }))
                const fieldsSync = await axios.get('/application/capsule/syncfields')
                setStatus((s) => ({ ...s, fields: fieldsSync.data.total }))
                setSyncing((s) => ({ ...s, fields: 2 }))

                setSyncing((s) => ({ ...s, lists: 1 }))
                const listsSync = await axios.get('/application/capsule/synclists')
                setStatus((s) => ({ ...s, segments: listsSync.data.total }))
                setSyncing((s) => ({ ...s, lists: 2 }))

                setSyncing((s) => ({ ...s, contacts: 1 }))
                PostHogFunctions.capture('import_completed', {
                    source: 'capsule'
                })
                const contactsSync = await axios.get('/application/capsule/synccontacts')
                setImportJob(contactsSync.data)
            }
        } catch (e) {
            console.log(e)
        }
    }

    const getJob = async () => {
        const res = await axios.get('/group/' + group + '/import/' + importJob.id)
        setImportJob(res.data.ImportJob)
        if (res.data.ImportJob.importStatus >= 20) {
            console.log('import complete', res.data.ImportJob.importStatus)
            setSyncing((s) => ({ ...s, contacts: 2 }))
            const timeTaken = Date.now() - importStartTime
            PostHogFunctions.capture('onboarding_sync_complete', {
                duration: timeTaken / 1000
            })
            getStatus()
        }
    }

    const updateSettings = async (newSettings) => {
        await axios.put('/accountMaster/extra', {
            capsuleOnboarding: { ...newSettings }
        })
    }

    useEffect(() => {
        if (showGroupOptions === false && showPasswordOptions === false) {
            const query = new URLSearchParams(window.location.search)
            startSync(query.get('resync') === 'true')
        }
    }, [showGroupOptions, showPasswordOptions])

    useEffect(() => {
        if (importTimer.current) clearTimeout(importTimer.current)
        if (importJob && importJob.importStatus < 20) {
            importTimer.current = setTimeout(getJob, 2000)
        }
    }, [importJob])

    useEffect(() => {
        ;(async () => {
            try {
                PostHogFunctions.capture('account_registration', {
                    referrer: 'Capsule'
                })
                await axios.get('/application/capsule/site') // this sets everything up server side
                const res3 = await axios.get('/application/account/app/8')

                setApp(res3.data.Application)

                const userRes = await axios.get('/accountUser/me')
                if (!userRes.data.AccountUserData.resetting) {
                    setShowPasswordOptions(false)
                } else {
                    setShowPasswordOptions(true)
                }

                const res = await axios.get('/group')
                setGroups(res.data.Groups)
                if (res.data.Groups.length && !res3.data.Application.appSettings.groupId) {
                    const foundGroup = res.data.Groups.find((g) => g.groupName === 'Capsule')
                    if (foundGroup) {
                        setGroup(foundGroup.id)
                        await axios.put('/application/account/' + res3.data.Application.id, {
                            appSettings: { ...res3.data.Application.appSettings, groupId: foundGroup.id }
                        })
                        setShowGroupOptions(false)
                    } else {
                        try {
                            const group = await createDefaultGroup(
                                userRes.data.AccountUserData,
                                account.accountMaster.accountName
                            )
                            setGroups((g) => [...g, group])
                            setGroup(group.id)
                            await axios.put('/application/account/' + res3.data.Application.id, {
                                appSettings: { ...res3.data.Application.appSettings, groupId: group.id }
                            })
                            setShowGroupOptions(false)
                        } catch (e) {
                            // its probably a disosable domain so get them to create a group manually
                            setNewGroup(
                                await createDefaultGroup(
                                    userRes.data.AccountUserData,
                                    account.accountMaster.accountName,
                                    true
                                )
                            )
                            setShowGroupOptions(true)
                        }
                    }
                } else if (!res3.data.Application.appSettings.groupId) {
                    // create a default group
                    try {
                        const group = await createDefaultGroup(
                            userRes.data.AccountUserData,
                            account.accountMaster.accountName
                        )
                        setGroups((g) => [...g, group])
                        setGroup(group.id)
                        await axios.put('/application/account/' + res3.data.Application.id, {
                            appSettings: { ...res3.data.Application.appSettings, groupId: group.id }
                        })
                        setShowGroupOptions(false)
                    } catch (e) {
                        // its probably a disosable domain so get them to create a group manually
                        setNewGroup(
                            await createDefaultGroup(
                                userRes.data.AccountUserData,
                                account.accountMaster.accountName,
                                true
                            )
                        )
                        setShowGroupOptions(true)
                    }
                }

                if (res3.data && res3.data.Application.appSettings && res3.data.Application.appSettings.groupId) {
                    setGroup(res3.data.Application.appSettings.groupId)
                    setShowGroupOptions(false)
                    getStatus()
                }
            } catch (e) {
                console.log(e)
            }
        })()

        return function () {
            if (importTimer.current) {
                clearTimeout(importTimer.current)
            }
        }
    }, [])

    if (!groups || !app)
        return (
            <div className='mar-t40'>
                <LoadingIcons iconType='2' />
            </div>
        )

    let templateClasses = 'landing-appconnect'
    let header = 'We are syncing your Capsule data...'
    let subheader = 'Hang tight while we get everything ready!'
    if (showPasswordOptions || showGroupOptions) {
        templateClasses += ' landing-appconnect__user'
        header = 'Finish setting up your account'
        subheader = 'We just need a few details to set up your account and start syncing your Capsule contacts.'
    }

    let version = 1
    if (account.accountMaster?.beta?.capsule_onboarding_filtering) {
        version = 2
    }

    if (version === 1) {
        return (
            <LandingPageTemplate paperClasses={templateClasses} top={<TopIcon />} header={header} subheader={subheader}>
                {(showPasswordOptions || showGroupOptions) && (
                    <Step1
                        password={password}
                        setPassword={setPassword}
                        passwordError={passwordError}
                        newsletterChecked={newsletterChecked}
                        setNewsletterChecked={setNewsletterChecked}
                        next={savePasswordAndGroup}
                    />
                )}
                {!showPasswordOptions && !showGroupOptions && (
                    <Step2 syncing={syncing} status={status} importJob={importJob} next={() => onFinish(importJob)} />
                )}
            </LandingPageTemplate>
        )
    }

    if (version === 2) {
        let loading = true
        let gap = false
        let total
        if (!showPasswordOptions && !showGroupOptions) {
            gap = true
            if (Object.values(syncing).some((v) => v !== 2) || !importJob) {
                loading = true
                header = <div className='loading'>We're getting things ready</div>
                subheader = `We're preparing your contacts. This may take a few minutes.`
            } else {
                loading = false
                total = importJob ? importJob.newSubscribersImported + importJob.recordsExist : 0
                header = `${FormatNumberFuncs.formatNumber(total)} email contacts added!`
                subheader = `Capsule and Transpond are now connected`
            }

            if (showContactOptions) {
                gap = false
                loading = false
                header = 'Choose your Active Marketing Contacts'
                subheader = 'You can change the status of your contacts at any time in Capsule and Transpond.'
            }
        }
        return (
            <LandingPageTemplate
                paperClasses={templateClasses}
                top={<TopIcon loading={loading} gap={gap} />}
                header={header}
                subheader={subheader}
            >
                {(showPasswordOptions || showGroupOptions) && (
                    <Step1
                        password={password}
                        setPassword={setPassword}
                        passwordError={passwordError}
                        newsletterChecked={newsletterChecked}
                        setNewsletterChecked={setNewsletterChecked}
                        next={savePasswordAndGroup}
                    />
                )}
                {!showPasswordOptions &&
                    !showGroupOptions &&
                    (showContactOptions ? (
                        <CapsuleSetupContacts
                            groupId={importJob?.GroupId}
                            totalContacts={total}
                            updateAppSettings={updateSettings}
                        />
                    ) : (
                        <CapsuleSetupImporting finished={!loading} next={() => setShowContactOptions(true)} />
                    ))}
            </LandingPageTemplate>
        )
    }
}

function TopIcon({ gap = false, loading = false }) {
    let appIconClass = 'landing-appconnect__app-icons__app'
    let siteIconClass = 'landing-appconnect__app-icons__site'
    let outerClass = 'landing-appconnect__app-icons'
    if (gap) {
        appIconClass += ' noLean'
        siteIconClass += ' noLean'
    }
    return (
        <div className={outerClass}>
            <div className={appIconClass}>
                <img src={CapsuleLogo} />
            </div>
            {gap && loading && <LoadingIcons />}
            {gap && !loading && (
                <Icons color='var(--cyan-500)' style={{ zIndex: 1, height: 20, width: 20 }} icon='tickCircle' />
            )}
            <div className={siteIconClass}>
                <img src={TranspondLogo} />
            </div>
        </div>
    )
}

function Step1({ password, setPassword, passwordError, newsletterChecked, setNewsletterChecked, next = () => {} }) {
    const [type, setType] = useState('password')

    useEffect(() => {
        PostHogFunctions.capture('onboarding_set_password')
    }, [])

    return (
        <>
            <TextInput
                type={type}
                name='password'
                label={i18n.t('login:login.password')}
                labelSub={i18n.t('login:reset.strong')}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                onEnterPress={next}
                error={passwordError}
                outerClassName='mar-b16'
                iconRight={
                    <Icons
                        icon='eye'
                        className={type === 'password' ? 'icons-cross' : 'icons-cross active'}
                        color='var(--neutral-900)'
                        onClick={() => {
                            setType(type === 'password' ? 'text' : 'password')
                        }}
                    />
                }
            />
            <CheckBox
                value='1'
                checked={newsletterChecked}
                onClick={() => {
                    if (newsletterChecked == false) {
                        setNewsletterChecked(true)
                    } else {
                        setNewsletterChecked(false)
                    }
                }}
            >
                I want to receive news and updates about Transpond
            </CheckBox>
            <Button
                className='button-primary button-fullwidth mar-t32'
                iconRight={<Icons icon='arrowRight' />}
                onClick={next}
            >
                Create Account
            </Button>
        </>
    )
}

function Step2({ syncing, status, importJob, next = () => {} }) {
    const [loading, setLoading] = useState(false)

    const total = importJob
        ? importJob.importStatus === 20
            ? importJob.recordsTotal
            : importJob.recordsInvalid +
              importJob.recordsExist +
              importJob.newSubscribersImported +
              importJob.recordsDupes
        : 0
    return (
        <>
            <div className='landing-appconnect__capsule_importsteps'>
                <Step
                    status={syncing.fields}
                    header='Custom Fields'
                    subheader='Capsule Custom Fields sync to contact data in Transpond.'
                    total={status.fields}
                />
                <Step
                    status={syncing.tags}
                    header='Tags'
                    subheader='Capsule Tags copy over to Transpond.'
                    total={status.tags}
                />
                <Step
                    status={syncing.fields}
                    header='Lists'
                    subheader='Capsule Lists become Segments and sync automatically.'
                    total={status.segments}
                />
                <Step
                    status={syncing.contacts}
                    header='Contacts'
                    subheader='Capsule organisations and people sync to Transpond.'
                    total={`${total} / ${importJob?.recordsTotal}`}
                />
            </div>
            <Button
                className='button-primary button-fullwidth'
                iconRight={<Icons icon='arrowRight' />}
                onClick={() => {
                    setLoading(true)
                    next()
                }}
                disabled={loading}
            >
                Start using Transpond
            </Button>
        </>
    )
}

function Step({ status, header, subheader, total }) {
    const totalString = typeof total === 'number' ? FormatNumberFuncs.formatNumber(total) : total
    return (
        <div className='step'>
            <div>
                {!status === 0 ||
                    (status < 2 && (
                        <div>
                            <LoadingIcons style={{ height: 20, width: 20 }} />
                        </div>
                    ))}
                {status === 2 && <Icons icon='tickCircle' color='var(--cyan-500)' />}
            </div>
            <div style={{ flexGrow: 1 }}>
                <h1>{header}</h1>
                <h3>{subheader}</h3>
            </div>
            {status === 2 && (
                <div>
                    <h2>{totalString}</h2>
                </div>
            )}
        </div>
    )
}
